import { currentQuarter, formatMinor, type TaxInsights, verificationCaveat } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, cn, Input } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  taxInsightsQueryOptions,
  taxPacksQuery,
  verifyTaxPack,
} from '../../shared/api/tax-queries.js';

/**
 * The tax page.
 *
 * The one number an owner wants is what they can claim this quarter. The
 * number beside it, which they want less and need more, is how much of that
 * claim is resting on something questionable. Showing the first without the
 * second is how a pleasant figure becomes a reassessment.
 *
 * Nothing here files anything.
 */

export function TaxPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';

  const [period, setPeriod] = useState(() => currentQuarter(new Date()));

  const insights = useQuery(taxInsightsQueryOptions(organizationId, period));
  const packs = useQuery(taxPacksQuery(organizationId));
  const queryClient = useQueryClient();

  const [verifying, setVerifying] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const verify = useMutation({
    mutationFn: (packId: string) => verifyTaxPack(organizationId, packId, note.trim()),
    onSuccess: () => {
      setVerifying(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['tax', organizationId] });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-content">Tax</h1>
        <p className="mt-1 text-sm text-content-muted">
          Worked out by rule, from dated legislation. Nothing here is filed and nothing is estimated
          by a model.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-content-muted">From</span>
          <Input
            type="date"
            value={period.from}
            onChange={(event) => setPeriod({ ...period, from: event.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-content-muted">To</span>
          <Input
            type="date"
            value={period.to}
            onChange={(event) => setPeriod({ ...period, to: event.target.value })}
          />
        </label>
      </div>

      {insights.isPending ? (
        <p className="text-sm text-content-muted">Working it out…</p>
      ) : insights.isError ? (
        <Alert tone="danger" title="Those figures could not be worked out">
          {insights.error instanceof Error ? insights.error.message : 'Unknown error.'}
        </Alert>
      ) : (
        <Insights data={insights.data} />
      )}

      <Card>
        <CardHeader
          title="Rule sets in force"
          description="A pack is a jurisdiction's rules and rates, versioned, with a source you can check."
        />
        <CardBody className="flex flex-col gap-2">
          {(packs.data ?? []).map((pack) => (
            <div
              key={pack.id}
              className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border pb-2 text-sm last:border-0"
            >
              <div>
                <span className="font-medium text-content">{pack.name}</span>{' '}
                <span className="text-content-muted">
                  {pack.code} {pack.version}
                </span>
                <span className="block text-xs text-content-subtle">
                  {pack.ruleCount} rule{pack.ruleCount === 1 ? '' : 's'}, {pack.rateCount} rate
                  {pack.rateCount === 1 ? '' : 's'}
                  {pack.sourceUrl === null ? null : (
                    <>
                      {' · '}
                      <a href={pack.sourceUrl} className="underline" rel="noreferrer">
                        source
                      </a>
                    </>
                  )}
                </span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span
                  className={cn(
                    'text-xs font-medium',
                    pack.verifiedAt === null ? 'text-warning' : 'text-success',
                  )}
                >
                  {pack.verifiedAt === null
                    ? 'Not checked yet'
                    : `Checked ${pack.verifiedAt.slice(0, 10)}`}
                </span>

                {pack.verifiedAt !== null ? null : verifying === pack.id ? (
                  <div className="flex gap-2">
                    <Input
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder="Checked against canada.ca by …"
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => verify.mutate(pack.id)}
                      disabled={note.trim().length < 10}
                      isLoading={verify.isPending}
                    >
                      Confirm
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setVerifying(pack.id)}>
                    I have checked this
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardBody>
      </Card>
    </div>
  );
}

function Insights({ data }: { data: TaxInsights }) {
  const caveat = verificationCaveat(data);

  return (
    <>
      {caveat === null ? null : (
        <Alert tone="warning" title="These figures rest on rules nobody has checked">
          {caveat}
        </Alert>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <Figure
          label="Credits available"
          value={formatMinor(data.itcAvailableMinor)}
          hint="HST paid that the rules say you can recover"
          tone="success"
        />
        <Figure
          label="Credits at risk"
          value={formatMinor(data.itcAtRiskMinor)}
          hint="Claimed on records with something unresolved"
          tone={data.itcAtRiskMinor === '0' ? undefined : 'warning'}
        />
        <Figure label="HST paid" value={formatMinor(data.taxPaidMinor)} hint="Across the period" />
      </div>

      <Card>
        <CardHeader title="Worth a look before filing" />
        <CardBody className="flex flex-col gap-2 text-sm">
          <Finding
            count={data.recordsMissingTax}
            singular="record shows no HST at all"
            plural="records show no HST at all"
            detail="Either the supplier charged none, or it was not read off the receipt. The second costs you the credit."
          />
          <Finding
            count={data.suppliersWithoutTaxNumber}
            singular="supplier has no GST/HST number on file"
            plural="suppliers have no GST/HST number on file"
            detail="The CRA can disallow a credit claimed without the supplier's registration number."
          />
          <Finding
            count={data.capitalTreatedAsExpense}
            singular="purchase over your capital threshold is being expensed"
            plural="purchases over your capital threshold are being expensed"
            detail="Worth confirming each one. Whether something is capital turns on useful life, which no threshold knows."
          />
          <Finding
            count={data.recordsFromUnverifiedPack}
            singular="record was decided by an unchecked rule set"
            plural="records were decided by an unchecked rule set"
            detail="The decisions are consistent and reproducible; the rates behind them have simply not been confirmed against the legislation."
          />
        </CardBody>
      </Card>
    </>
  );
}

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  tone?: 'success' | 'warning' | undefined;
}) {
  return (
    <div className="rounded-[var(--radius-surface)] border border-border bg-surface px-4 py-3">
      <p className="text-xs text-content-muted">{label}</p>
      <p
        className={cn(
          'tabular text-2xl font-semibold',
          tone === 'warning'
            ? 'text-warning'
            : tone === 'success'
              ? 'text-success'
              : 'text-content',
        )}
      >
        {value}
      </p>
      <p className="mt-1 text-xs text-content-subtle">{hint}</p>
    </div>
  );
}

function Finding({
  count,
  singular,
  plural,
  detail,
}: {
  count: number;
  singular: string;
  plural: string;
  detail: string;
}) {
  if (count === 0) return null;
  return (
    <div>
      <p className="text-content">
        <span className="tabular font-semibold">{count}</span> {count === 1 ? singular : plural}.
      </p>
      <p className="text-xs text-content-muted">{detail}</p>
    </div>
  );
}
